/**
 * The Gen V battle text box (`.win-light`): one line at a time in plain English, the numbers in a
 * small sub-line under it, a typewriter, and a ▼ when it waits. A tap finishes the line that is
 * typing, else moves on, held line or not. Lines queue: `say` resolves when its line is done
 * (tapped past, or read at reading speed), so the screen can hold the play on it. When lines
 * wait behind the one on screen, the box catches up: whole lines, shorter reads. `prompt` shows a
 * line that waits for the action row instead, and `choice` one that waits for one of its buttons.
 */
import type { BoxLine } from '../screen/reader';

export interface TextBoxTiming {
  /** Characters a second; 0 shows the whole line at once (reduced motion). */
  cps: () => number;
  /** How long a line that does not hold stays once typed, in ms at 1x. */
  readMs: () => number;
  /** Playback speed: 2 halves typing and reading. */
  speed: () => number;
}

interface Queued {
  line: BoxLine;
  done: () => void;
}

export class TextBox {
  private readonly text: HTMLElement;
  private readonly numbers: HTMLElement;
  private readonly next: HTMLButtonElement;
  private readonly choices: HTMLElement;
  /** Screen readers get each line once, whole, not letter by letter. */
  private readonly live: HTMLElement;
  private queue: Queued[] = [];
  private current: Queued | null = null;
  private typed = 0;
  private typing: ReturnType<typeof setInterval> | undefined;
  private reading: ReturnType<typeof setTimeout> | undefined;
  /** Every line shown, in order (what the tests read). */
  readonly shown: BoxLine[] = [];

  constructor(
    private readonly root: HTMLElement,
    private readonly timing: TextBoxTiming,
  ) {
    const part = <T extends HTMLElement>(sel: string) => {
      const e = root.querySelector<T>(sel);
      if (!e) throw new Error(`the text box needs ${sel}`);
      return e;
    };
    this.text = part('.tb-text');
    this.numbers = part('.tb-numbers');
    this.next = part<HTMLButtonElement>('.tb-next');
    this.choices = part('.tb-choices');
    this.live = part('.tb-live');
    root.addEventListener('click', (ev) => {
      if ((ev.target as HTMLElement).closest('.tb-choices')) return;
      this.tap();
    });
  }

  /** Queue a line; resolves when it is done. */
  say(line: BoxLine): Promise<void> {
    return new Promise((done) => {
      this.queue.push({ line, done });
      if (!this.current) this.advance();
    });
  }

  /**
   * Say `line` as soon as the box catches up (the result line at the whistle, slice 8c). Lines
   * still waiting are completed at once, each shown whole with no typing; the last of them stays
   * one read (`readMs`) so it is seen before `line` lands. Resolves when `line` is done.
   */
  sayNow(line: BoxLine): Promise<void> {
    if (this.queue.length === 0) return this.say(line);
    const behind = [...(this.current ? [this.current] : []), ...this.queue];
    const last = behind.at(-1) as Queued;
    this.queue = [];
    this.stop();
    for (const q of behind) {
      // The line on screen was already counted when it started.
      if (q !== this.current) this.shown.push(q.line);
      q.done();
    }
    this.current = { line: { ...last.line, hold: false }, done: () => {} };
    this.render(last.line, Infinity, false);
    this.reading = setTimeout(() => this.complete(), this.timing.readMs() / this.timing.speed());
    return new Promise((done) => this.queue.push({ line, done }));
  }

  /** A tap: finish typing, else move on. */
  tap(): void {
    if (!this.current) return;
    if (this.typing !== undefined) {
      this.finishTyping();
      return;
    }
    this.complete();
  }

  /** Finish every queued line at once (a skipped play); the last one stays on screen. */
  flush(): void {
    const all = [...(this.current ? [this.current] : []), ...this.queue];
    this.queue = [];
    this.stop();
    this.current = null;
    for (const q of all) {
      this.shown.push(q.line);
      q.done();
    }
    const last = all.at(-1);
    if (last) this.render(last.line, last.line.text.length, false);
  }

  /** A line that waits on the action row, not a tap: the queue is dropped. */
  prompt(text: string, numbers?: string): void {
    this.reset();
    this.render(
      { text, ...(numbers !== undefined ? { numbers } : {}), hold: false },
      Infinity,
      false,
    );
  }

  /** A question with buttons in the box; resolves with the chosen option's value. */
  choice(text: string, options: readonly { label: string; value: string }[]): Promise<string> {
    this.prompt(text);
    return new Promise((pick) => {
      for (const o of options) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'btn tb-choice';
        b.textContent = o.label;
        b.addEventListener('click', () => {
          this.choices.replaceChildren();
          this.choices.hidden = true;
          pick(o.value);
        });
        this.choices.append(b);
      }
      this.choices.hidden = false;
    });
  }

  /** Drop everything (a new game); pending lines resolve so nothing waits on them. */
  reset(): void {
    const all = [...(this.current ? [this.current] : []), ...this.queue];
    this.queue = [];
    this.stop();
    this.current = null;
    for (const q of all) q.done();
    this.choices.replaceChildren();
    this.choices.hidden = true;
    this.render({ text: '', hold: false }, 0, false);
  }

  /** The box is waiting for a tap (a held line, typed out). */
  get waiting(): boolean {
    return !this.next.hidden;
  }

  private advance(): void {
    const q = this.queue.shift();
    this.current = q ?? null;
    if (!q) return;
    this.shown.push(q.line);
    const cps = this.timing.cps() * this.timing.speed();
    // Behind the field (lines waiting): print whole lines, as fast as they can be read.
    if (cps <= 0 || !Number.isFinite(cps) || this.queue.length > 0) {
      this.render(q.line, Infinity, false);
      this.typed = q.line.text.length;
      this.afterTyping();
      return;
    }
    this.typed = 0;
    this.render(q.line, 0, false);
    // Letters come from the time since the line started, not from the count of ticks: on a busy
    // main thread (the field rendering at DPR 3) ticks arrive late and the line must not stretch.
    const start = Date.now();
    this.typing = setInterval(() => {
      this.typed = Math.max(this.typed + 1, Math.floor(((Date.now() - start) * cps) / 1000));
      if (this.typed >= q.line.text.length) this.finishTyping();
      else this.render(q.line, this.typed, false);
    }, 1000 / cps);
  }

  private finishTyping(): void {
    clearInterval(this.typing);
    this.typing = undefined;
    this.afterTyping();
  }

  private afterTyping(): void {
    const q = this.current;
    if (!q) return;
    this.render(q.line, Infinity, q.line.hold);
    if (!q.line.hold) {
      // The more lines wait behind this one, the shorter it stays.
      const ms = this.timing.readMs() / this.timing.speed() / (1 + this.queue.length);
      this.reading = setTimeout(() => this.complete(), ms);
    }
  }

  private complete(): void {
    const q = this.current;
    this.stop();
    this.current = null;
    q?.done();
    this.advance();
  }

  private stop(): void {
    clearInterval(this.typing);
    clearTimeout(this.reading);
    this.typing = undefined;
    this.reading = undefined;
  }

  private render(line: BoxLine, chars: number, waiting: boolean): void {
    const whole = [line.text, line.numbers].filter(Boolean).join(' ');
    if (this.live.textContent !== whole) this.live.textContent = whole;
    this.text.textContent = chars >= line.text.length ? line.text : line.text.slice(0, chars);
    // The numbers appear with the line's last letter, as the games print a line whole.
    const full = chars >= line.text.length;
    this.numbers.textContent = full ? (line.numbers ?? '') : '';
    this.numbers.hidden = !full || !line.numbers;
    this.root.classList.toggle('tb-super', !!line.superEffective);
    this.next.hidden = !waiting;
  }
}
